/**
 * MineList — the user's own reports on this project.
 *
 * Polls every 30 s while mounted (Sentry-style staleTime + manual
 * refresh). Empty state when the submitter has no thread yet (404 from
 * the backend is mapped to []). Click a row to open the detail view.
 */

import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import type { ApiClient } from '../api/client'
import type { WidgetReportRow } from '../types'
import type { StringKey } from './i18n'
import { KpiStrip, type KpiFilter, rowsMatchingFilter } from './KpiStrip'
import { ReportRow } from './ReportRow'

interface MineListProps {
  api: ApiClient
  externalId: string
  strings: Record<StringKey, string>
  onSelect: (report: WidgetReportRow) => void
}

const POLL_MS = 30_000

export function MineList({ api, externalId, strings, onSelect }: MineListProps) {
  const [rows, setRows] = useState<WidgetReportRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState<KpiFilter>('all')
  const mountedRef = useRef(true)

  const fetchRows = async () => {
    setRefreshing(true)
    setError(null)
    try {
      const next = await api.listMine(externalId)
      if (!mountedRef.current) return
      setRows(next)
    } catch (err) {
      // Don't surface raw API error messages to the user — they used to
      // render as e.g. `listMine failed: 403 {"detail":"Public API key…"}`
      // straight into the panel. Friendly fallback in the UI, raw error
      // in the console for the developer.
      if (typeof console !== 'undefined') console.warn('[mhosaic] listMine:', err)
      if (!mountedRef.current) return
      setError(strings['mine.error'])
    } finally {
      if (mountedRef.current) setRefreshing(false)
    }
  }

  useEffect(() => {
    mountedRef.current = true
    void fetchRows()
    const timer = setInterval(() => {
      void fetchRows()
    }, POLL_MS)
    return () => {
      mountedRef.current = false
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId])

  const isEmpty = rows !== null && rows.length === 0
  const isLoading = rows === null && !error
  const visibleRows = rows ? rowsMatchingFilter(rows, filter) : null
  const visibleEmpty = !!rows && rows.length > 0 && (visibleRows?.length ?? 0) === 0

  return (
    <div class="mine-list">
      <div class="mine-list-header">
        <h2>{strings['tab.mine']}</h2>
        <button
          type="button"
          class="btn"
          onClick={() => {
            void fetchRows()
          }}
          disabled={refreshing}
        >
          {refreshing ? strings['mine.loading'] : strings['mine.refresh']}
        </button>
      </div>
      {rows && rows.length > 0 && (
        <KpiStrip rows={rows} filter={filter} onFilter={setFilter} strings={strings} />
      )}
      {isLoading && <div class="mine-loading">{strings['mine.loading']}</div>}
      {error && <div class="error">{error}</div>}
      {isEmpty && (
        <div class="mine-empty">
          <strong>{strings['mine.empty.title']}</strong>
          <p>{strings['mine.empty.body']}</p>
        </div>
      )}
      {visibleEmpty && (
        <div class="mine-empty">
          <p>{strings['mine.filter.empty']}</p>
        </div>
      )}
      {visibleRows && visibleRows.length > 0 && (
        <ul class="mine-rows">
          {visibleRows.map((row) => (
            <li>
              <ReportRow row={row} strings={strings} onClick={() => onSelect(row)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
